/*
 * Subscriber email popup.
 *
 * Opens once the visitor has both spent a minimum time on the page AND scrolled past a threshold,
 * so it never interrupts someone still reading and never runs before LCP. Dismissal is remembered
 * for 48h; a successful subscribe is remembered permanently.
 *
 * The section only renders for logged-out visitors (sections/bitco-email-popup.liquid), so this
 * file is never requested for a signed-in customer.
 *
 * SUBMIT IS A NATIVE FORM POST, NOT FETCH. An earlier version posted the form with fetch() and
 * checked the redirect URL. Against the real storefront Shopify rejected those requests (403, then
 * 400 on retry) while still processing some of them, so the customer was subscribed and tagged but
 * the popup reported failure. Shopify's storefront form endpoints expect a real navigation; a
 * background fetch does not carry the Sec-Fetch-Mode: navigate that a form submit does, and those
 * headers cannot be set from script. The existing bitco-newsletter band posts natively and works,
 * so this does the same and picks the result back up after the reload via ?customer_posted=true.
 *
 * Storage:
 *   localStorage   bitco_sub_popup    {"s":"d"|"s","t":<epoch ms>}  dismissal / subscription
 *   localStorage   bitco_epop_emails  ["a@b.com", ...]              addresses subscribed here
 *   sessionStorage bitco_epop_pending {"e":"<email>","t":<epoch ms>} survives the submit redirects
 *
 * Every storage access is wrapped. Missing, corrupt or unavailable storage (Safari private mode,
 * blocked cookies) all resolve to "never shown": the feature fails OPEN, which is the right
 * direction — someone seeing the offer twice beats someone who can never see it.
 */
(function () {
  var root = document.querySelector('[data-bitco-epop]');
  if (!root) return;

  var KEY = 'bitco_sub_popup';
  var PENDING = 'bitco_epop_pending';
  var SEEN = 'bitco_epop_emails';
  var DISMISSED = 'd';
  var SUBSCRIBED = 's';

  var delayMs = (parseInt(root.getAttribute('data-delay'), 10) || 0) * 1000;
  var scrollPct = parseInt(root.getAttribute('data-scroll'), 10) || 0;
  var suppressMs = (parseInt(root.getAttribute('data-suppress-hours'), 10) || 48) * 3600 * 1000;

  var overlay = root.querySelector('[data-bitco-epop-overlay]');
  var dialog = root.querySelector('[data-bitco-epop-dialog]');
  var tab = root.querySelector('[data-bitco-epop-tab]');
  var closeBtn = root.querySelector('[data-bitco-epop-close]');
  var form = root.querySelector('form');
  var emailInput = root.querySelector('[data-bitco-epop-email]');
  var submitBtn = root.querySelector('[data-bitco-epop-submit]');
  var errorEl = root.querySelector('[data-bitco-epop-error]');
  var formView = root.querySelector('[data-bitco-epop-view="form"]');
  var successView = root.querySelector('[data-bitco-epop-view="success"]');

  if (!overlay || !dialog || !tab || !form) return;

  /* ---------- storage ---------- */

  function readState() {
    try {
      var parsed = JSON.parse(localStorage.getItem(KEY));
      return parsed && typeof parsed.t === 'number' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ s: state, t: Date.now() }));
    } catch (e) {}
  }

  function normalise(email) {
    return String(email || '').trim().toLowerCase();
  }

  function seenEmails() {
    try {
      var list = JSON.parse(localStorage.getItem(SEEN));
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function rememberEmail(email) {
    var value = normalise(email);
    if (!value) return;
    try {
      var list = seenEmails();
      if (list.indexOf(value) === -1) {
        list.push(value);
        localStorage.setItem(SEEN, JSON.stringify(list.slice(-20)));
      }
    } catch (e) {}
  }

  // Time-boxed rather than consumed on first read. Shopify redirects the post to
  // ?customer_posted=true, and this storefront then runs its own locale redirect on top of that
  // (/ -> /en-be), so the confirmation page can load TWICE. A one-shot flag was spent by the first
  // load, leaving the second — the one the shopper actually ends up looking at — with no idea the
  // popup had just submitted, so the success state never appeared.
  var PENDING_TTL = 120000;

  function readPending() {
    try {
      var parsed = JSON.parse(sessionStorage.getItem(PENDING));
      if (!parsed || typeof parsed.t !== 'number') return null;
      if (Date.now() - parsed.t > PENDING_TTL) {
        clearPending();
        return null;
      }
      return parsed.e || '';
    } catch (e) {
      return null;
    }
  }

  function clearPending() {
    try { sessionStorage.removeItem(PENDING); } catch (e) {}
  }

  var stored = readState();
  var hasSubscribed = !!stored && stored.s === SUBSCRIBED;
  var isSuppressed = !!stored && stored.s === DISMISSED && Date.now() - stored.t < suppressMs;

  /* ---------- focus handling ---------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var lastFocused = null;

  function visibleFocusable() {
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    var items = visibleFocusable();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- on-screen keyboard ---------- */

  // On mobile the dialog is a bottom sheet pinned to the bottom of the LAYOUT viewport, which an
  // on-screen keyboard does not shrink — so by default the keyboard covers the email field the
  // visitor just tapped. visualViewport reports the actually-visible area; the difference is the
  // overlap to lift by. Writes `bottom`, not `transform`, because the open/close animation already
  // owns this element's transform.
  function syncKeyboardOffset() {
    var vv = window.visualViewport;
    if (!vv || dialog.hidden) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      dialog.style.bottom = '';
      return;
    }
    var overlap = window.innerHeight - vv.height - vv.offsetTop;
    dialog.style.bottom = overlap > 0 ? Math.round(overlap) + 'px' : '';
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncKeyboardOffset);
    window.visualViewport.addEventListener('scroll', syncKeyboardOffset);
  }

  /* ---------- open / close ---------- */

  function open() {
    if (dialog.hidden === false) return;

    lastFocused = document.activeElement;
    overlay.hidden = false;
    dialog.hidden = false;
    tab.hidden = true;

    // Slides the mobile tab bar out of the way and locks background scroll, reusing the hand-off
    // the PDP sticky add-to-cart bar and the filter sheet already use (assets/bitco-base.css).
    document.body.classList.add('bitco-popup-open');

    document.addEventListener('keydown', onKeydown);

    // Focus moves immediately, NOT inside the rAF below: rAF does not run in a background tab, so
    // deferring it would leave a timer-opened popup with focus still out in the page behind it.
    var target = successView && !successView.hidden
      ? successView.querySelector('[data-bitco-epop-success-cta]')
      : closeBtn;
    if (target) target.focus();

    // rAF so the element paints in its closed-transform state before the class animates it;
    // without it the transition is skipped and the dialog snaps in.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
  }

  function close() {
    if (dialog.hidden) return;

    root.classList.remove('is-open');
    // The post is finished being reported once they close it, so the flag can go now. Clearing it
    // on first read instead would lose the confirmation to the storefront's locale redirect.
    clearPending();
    document.removeEventListener('keydown', onKeydown);
    document.body.classList.remove('bitco-popup-open');
    dialog.style.bottom = '';

    // Only record a dismissal if they left without subscribing. Closing the success state must not
    // downgrade the permanent record to a 48h one.
    if (!hasSubscribed) {
      writeState(DISMISSED);
      tab.hidden = false;
    }

    var done = function () {
      overlay.hidden = true;
      dialog.hidden = true;
      dialog.removeEventListener('transitionend', done);
    };

    // transitionend does not fire when motion is reduced or the tab is backgrounded, so the
    // timeout is the guarantee rather than a fallback.
    dialog.addEventListener('transitionend', done);
    setTimeout(done, 260);

    if (lastFocused === closeBtn || dialog.contains(lastFocused)) lastFocused = null;
    if (!tab.hidden) tab.focus();
    else if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ---------- messages ---------- */

  // Setting textContent is what makes the role="alert" region announce. Unhiding a node that
  // already held its text is not a content change, so it would be silent to a screen reader.
  function showMessage(attr) {
    if (!errorEl) return;
    errorEl.textContent = errorEl.getAttribute(attr) || errorEl.getAttribute('data-message') || '';
    errorEl.hidden = false;
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
    }
  }

  function showSuccess() {
    hasSubscribed = true;
    writeState(SUBSCRIBED);
    // Retire the reopen tab for good. Without this it survives the one path that matters: dismiss
    // once (which reveals the tab), come back through the tab, subscribe — the tab is already on
    // screen, so close() leaving it alone is not enough.
    tab.hidden = true;
    if (formView) formView.hidden = true;
    if (successView) successView.hidden = false;
  }

  /* ---------- submit ---------- */

  form.addEventListener('submit', function (event) {
    var value = normalise(emailInput && emailInput.value);

    // Shopify's newsletter form reports no server-side "this address is already on the list"
    // signal — an existing subscriber posts exactly like a new one. Asking the server would mean
    // building an endpoint that confirms whether an address is a customer, which is email
    // enumeration and not something to expose. So this is answered locally: addresses subscribed
    // from THIS browser. It catches the case that actually annoys people, re-entering the address
    // they just used, and silently does the normal thing for anything it has not seen.
    if (value && seenEmails().indexOf(value) !== -1) {
      event.preventDefault();
      showMessage('data-message-known');
      return;
    }

    if (errorEl) errorEl.hidden = true;
    if (emailInput) emailInput.removeAttribute('aria-invalid');

    // Survives the full page reload that the native submit causes, so the reloaded page knows the
    // popup submitted (rather than the newsletter band) and which address it was.
    try {
      sessionStorage.setItem(PENDING, JSON.stringify({ e: value, t: Date.now() }));
    } catch (e) {}

    if (submitBtn) {
      submitBtn.disabled = true;
      var busy = submitBtn.getAttribute('data-label-busy');
      if (busy) submitBtn.textContent = busy;
    }
    // No preventDefault: the browser performs a real navigation, which is the only thing Shopify's
    // form endpoint reliably accepts.
  });

  /* ---------- wiring ---------- */

  closeBtn && closeBtn.addEventListener('click', close);
  tab.addEventListener('click', open);
  // Overlay only. Clicks inside the card land on the dialog, which is a sibling, not a child.
  overlay.addEventListener('click', close);

  /* ---------- post-submit landing ---------- */

  // Shopify redirects a successful customer form post back to this page with ?customer_posted=true.
  var posted = location.search.indexOf('customer_posted=true') !== -1;
  var pending = posted ? readPending() : null;

  if (posted) {
    hasSubscribed = true;
    writeState(SUBSCRIBED);
    rememberEmail(pending);
  }

  // The server re-rendered the form with errors (invalid address, etc). Show it.
  if (errorEl && errorEl.hasAttribute('data-server-error')) {
    open();
    showMessage('data-message');
    return;
  }

  if (pending) {
    // This popup submitted, so confirm it. Any other customer_posted=true on the page belongs to
    // the newsletter band, which shows its own confirmation — staying closed avoids two at once.
    showSuccess();
    open();
    return;
  }

  /* ---------- trigger ---------- */

  if (hasSubscribed) return;

  if (isSuppressed) {
    // Still reachable, just not pushed at them.
    tab.hidden = false;
    return;
  }

  var timeReady = delayMs === 0;
  var scrollReady = scrollPct === 0;
  var ticking = false;

  function maybeOpen() {
    if (!timeReady || !scrollReady) return;
    open();
  }

  function measure() {
    ticking = false;
    if (scrollReady) return;

    var scrollable = document.documentElement.scrollHeight - window.innerHeight;
    // A page shorter than the viewport can never reach any threshold, so treat it as satisfied
    // rather than letting the popup become unreachable.
    var pct = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
    if (pct < scrollPct) return;

    // Detach as soon as the threshold is crossed, not when the popup finally opens, so a fast
    // scroller does not keep running this every frame until the delay elapses.
    scrollReady = true;
    window.removeEventListener('scroll', onScroll);
    maybeOpen();
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  }

  if (!scrollReady) {
    window.addEventListener('scroll', onScroll, { passive: true });
    measure();
  }

  if (!timeReady) {
    setTimeout(function () {
      timeReady = true;
      maybeOpen();
    }, delayMs);
  } else {
    maybeOpen();
  }
})();
